import { NavLink } from 'react-router-dom'

export interface TagProps {
	readonly text?: string | undefined;
	readonly children?: React.ReactNode | undefined;
	readonly style?: 'primary' | 'secondary' | 'warning' | 'outline' | 'outline-dark' | 'success' | 'danger' | undefined;
	readonly disabled?: boolean | undefined;
	readonly to?: string | {pathname: string; relative?: boolean} | undefined;
	readonly target?: string | undefined;
	readonly customClass?: string | undefined;
	readonly onClick?: (() => void) | undefined;
}

export default function Tag({ text, customClass = '', children, style = 'primary', disabled = false, to, target, onClick }: TagProps) {
	const defaultClass = 'flex items-center rounded-full px-3 py-1 m-1 max-w-full text-sm transition-all duration-200 ease-in-out'

	const stylesClasses = {
		primary: 'bg-primary text-white ',
		secondary: 'bg-secondary border border-blue-200 text-blue-500 ',
		warning: 'bg-yellow-400 text-black ',
		danger: 'bg-red-500 text-white ',
		outline: 'bg-white border border-gray-200 text-gray-400 ',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'outline-dark': 'bg-transparent border border-gray-300 text-white',
		success: 'bg-green-500 text-white'
	}

	const clickableStylesClasses = {
		primary: 'hover:bg-primary-hover active:bg-primary-active',
		secondary: 'hover:bg-secondary-hover active:bg-secondary-active',
		warning: 'hover:bg-yellow-500 active:bg-yellow-500',
		danger: 'hover:bg-red-600 bg-red-500 text-white',
		outline: 'hover:bg-gray-100 border',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'outline-dark': 'hover:bg-[#f9fafb0d]',
		success: 'hover:bg-green-500 active:bg-green-500'
	}

	let styleClassName = stylesClasses[style]

	if (to ?? onClick) {
		styleClassName += ' cursor-pointer ' + clickableStylesClasses[style]
	}

	if (disabled) {
		styleClassName += ' cursor-not-allowed'
	}

	function handleClick() {
		if (disabled) {
			return
		}

		if (onClick) {
			onClick()
		}
	}

	if (to) {
		if (typeof to === 'object') {
			return (
				<NavLink
					to={ to.pathname }
					relative={ to.relative ? 'path' : 'route' }
					target={ target }
					className={ `${defaultClass} ${styleClassName} ${customClass}` }
					onClick={ handleClick }
				>
					{ children }
					{ text }
				</NavLink>
			)
		}

		return (
			<a
				href={ to }
				target={ target }
				className={ `${defaultClass} ${styleClassName} ${customClass}` }
				onClick={ handleClick }
			>
				{ children }
				{ text }
			</a>
		)
	}

	return (
		<div
			className={ `${defaultClass} ${styleClassName} ${customClass}` }
			onClick={ handleClick }
		>
			{ children }
			{ text }
		</div>
	)
}
